import {
  EuiBadge,
  EuiBasicTable,
  EuiFlexGroup,
  EuiFlexItem,
  EuiTableComputedColumnType,
  EuiTableRowProps,
} from "@inscopix/ideas-eui";
import { formatMetadatumKey } from "./MetadataTable.helpers";
import { Tooltip } from "components/Tooltip/Tooltip";
import { memo, ReactNode } from "react";
import { MetadatumReferenceSelectorGdtProps } from "./MetadatumReferenceSelectorGdt";

type Metadatum = {
  key: string;
  name: string;
  values: (string | number | boolean | null)[];
};

interface MetadataTableGdtProps<T extends Metadatum> {
  isLoading: boolean;
  isError: boolean;
  items: T[];
  onSelect: (
    metadatumKey: Metadatum["key"],
    value: string | number | boolean | null,
  ) => void;
  currentReference: MetadatumReferenceSelectorGdtProps["currentReference"];
  renderValueTooltip: (
    metadatumKey: Metadatum["key"],
    value: string | number | boolean | null,
  ) => ReactNode;
}

/** Component that renders a table of metadatum key-value pairs */
export const MetadataTableGdt = memo(function MetadataTable<
  T extends Metadatum,
>({
  isLoading,
  isError,
  items,
  onSelect,
  currentReference,
  renderValueTooltip,
}: MetadataTableGdtProps<T>) {
  const columns: EuiTableComputedColumnType<T>[] = [
    {
      name: "Name",
      render: ({ name, key }) => (
        <EuiFlexGroup direction="column" gutterSize="none">
          <EuiFlexItem>
            <b>{name}</b>
          </EuiFlexItem>
          <EuiFlexItem>
            <code>{formatMetadatumKey(key)}</code>
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
    {
      name: "Value",
      render: (metadatum) => {
        return (
          <EuiFlexGroup gutterSize="s" wrap style={{ overflowX: "scroll" }}>
            {metadatum.values.map((value) => {
              return (
                <Tooltip
                  key={value?.toString()}
                  content={renderValueTooltip(metadatum.key, value)}
                >
                  <EuiFlexItem grow={false}>
                    <EuiBadge
                      color={
                        currentReference?.metadataKey === metadatum.key &&
                        value === currentReference.value
                          ? "primary"
                          : "hollow"
                      }
                      onClick={() => onSelect(metadatum.key, value)}
                      onClickAriaLabel="Select value"
                      style={{ cursor: "pointer", fontWeight: "normal" }}
                      title=""
                    >
                      {value}
                    </EuiBadge>
                  </EuiFlexItem>
                </Tooltip>
              );
            })}
          </EuiFlexGroup>
        );
      },
    },
  ];

  return (
    <EuiBasicTable
      items={items}
      columns={columns}
      compressed
      loading={isLoading}
      error={isError ? "Failed to load metadata" : undefined}
      noItemsMessage={isLoading ? "Loading..." : "No metadata found"}
      rowProps={(metadatum): Partial<EuiTableRowProps> => ({
        isSelected: metadatum.key === currentReference?.metadataKey,
      })}
      responsive={false}
    />
  );
});
